import React, { useState, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import useAxios from 'axios-hooks';
import {
  Dimmer,
  Loader,
  Container,
  Grid,
  Form,
  Table,
  Header,
  Button,
  Segment
} from 'semantic-ui-react';
import DateUtils from '../../utils/dateUtils';
import { ownerSelector } from '../../selectors';
import useOwnerProtection from '../../hooks/useOwnerProtection';
import DatepickerField from '../../components/Fields/DatepickerField';
import RoomTypeRow from '../../components/PropertyReport/RoomTypeRow';
import RoomRow from '../../components/PropertyReport/RoomRow';
import './OwnerPropertyReportView.css';

const useStyles = createUseStyles(
  {
    root: {
      flex: 1,
      padding: '2rem'
    }
  },
  { name: 'OwnerPropertyReportView' }
);

const initialState = {
  errors: {},
  data: {
    startDate: ''
  }
};

const OwnerPropertyReportView = () => {
  useOwnerProtection();

  const classes = useStyles();
  const { selectedRoomId } = ownerSelector();

  const [state, setState] = useState(initialState);
  const { errors, data } = state;
  const { startDate, endDate, createdDate, createdEndDate } = data;
  const [reportData, setReportData] = useState();
  const [roomData, setRoomData] = useState();
  const [detailData, setDetailData] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const [, execute] = useAxios(
    {
      url: '/api/listPropertyRevenueData',
      method: 'POST'
    },
    { manual: true }
  );

  const [, execute2] = useAxios(
    {
      url: '/api/getRoomRevenueData',
      method: 'POST'
    },
    { manual: true }
  );

  const [, execute3] = useAxios(
    {
      url: '/api/findPropertyReportRoomDetail',
      method: 'POST'
    },
    { manual: true }
  );

  const onError = () => {};

  const onSuccess = response => {
    setReportData(response.data);
  };

  const onSuccess2 = response => {
    setRoomData(response.data);
  };

  const onSuccess3 = response => {
    setDetailData(response.data);
  };

  const onRoomClick = async item => {
    try {
      setIsLoading(true);
      const response = await execute3({
        data: {
          startDate:
            startDate != null ? DateUtils.formatDate2(startDate) : null,
          endDate: endDate != null ? DateUtils.formatDate2(endDate) : null,
          createdStartDate:
            createdDate != null ? DateUtils.formatDate2(createdDate) : null,
          createdEndDate:
            createdEndDate != null
              ? DateUtils.formatDate2(createdEndDate)
              : null,
          roomId: item.roomId
        }
      });
      onSuccess3(response);
    } catch (_error) {
      onError();
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const requestData = {
        startDate: startDate != null ? DateUtils.formatDate2(startDate) : null,
        endDate: endDate != null ? DateUtils.formatDate2(endDate) : null,
        createdStartDate:
          createdDate != null ? DateUtils.formatDate2(createdDate) : null,
        createdEndDate:
          createdEndDate != null ? DateUtils.formatDate2(createdEndDate) : null,
        roomId: selectedRoomId
      };

      const [reportResponse, roomResponse] = await Promise.all([
        execute({ data: requestData }),
        execute2({ data: requestData })
      ]);

      onSuccess(reportResponse);
      onSuccess2(roomResponse);
    } catch (_error) {
      onError();
    } finally {
      setIsLoading(false);
    }
  };

  const onChange = (_event, { id, value }) => {
    setState({
      ...state,
      data: { ...data, [id]: value },
      errors: { ...errors, [id]: null }
    });
  };

  useEffect(() => {
    setState({
      ...state,
      data: { ...data, roomId: selectedRoomId }
    });
  }, [selectedRoomId]);

  return (
    <div className={classes.root}>
      <h1>Property Report</h1>
      <Dimmer.Dimmable dimmed={isLoading}>
        <Dimmer
          active={isLoading}
          inverted
          className="owner-property-report-dimmer"
        >
          <Loader inverted content="Loading Data..." />
        </Dimmer>
        <Container fluid style={{ padding: '40px 60px' }}>
          <Grid>
            <Grid.Row verticalAlign="middle">
              <Grid.Column width={5}>
                <div
                  style={{
                    marginTop: 60,
                    lineHeight: '32px',
                    fontSize: 18,
                    fontWeight: 600
                  }}
                >
                  <div>CY = Current Year</div>
                  <div>PY = Previous Year</div>
                  <div>ADR = Average Daily Rate</div>
                </div>
              </Grid.Column>

              <Grid.Column width={11}>
                <Form onSubmit={onSubmit}>
                  <Form.Group widths="equal">
                    <DatepickerField
                      id="startDate"
                      label="Start Date"
                      placeholder="Select Date"
                      value={startDate}
                      onChange={onChange}
                    />
                    <DatepickerField
                      id="endDate"
                      label="End Date"
                      placeholder="Select Date"
                      value={endDate}
                      onChange={onChange}
                    />
                    <DatepickerField
                      id="createdDate"
                      label="Created Date"
                      placeholder="Select Date"
                      value={createdDate}
                      onChange={onChange}
                    />
                    <DatepickerField
                      id="createdEndDate"
                      label="Created End Date"
                      placeholder="Select Date"
                      value={createdEndDate}
                      onChange={onChange}
                    />
                    <Form.Field
                      style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        marginBottom: 0
                      }}
                    >
                      <Button type="submit" primary>
                        Search
                      </Button>
                    </Form.Field>
                  </Form.Group>
                </Form>
              </Grid.Column>
            </Grid.Row>
          </Grid>

          <div style={{ overflowX: 'auto', marginTop: 30 }}>
            <Table celled structured>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Room</Table.HeaderCell>
                  <Table.HeaderCell>Room Type</Table.HeaderCell>
                  <Table.HeaderCell>Rental Revenue CY</Table.HeaderCell>
                  <Table.HeaderCell>Rental Revenue PY</Table.HeaderCell>
                  <Table.HeaderCell>+/-</Table.HeaderCell>
                  <Table.HeaderCell>Renter Nights CY</Table.HeaderCell>
                  <Table.HeaderCell>Renter Nights PY</Table.HeaderCell>
                  <Table.HeaderCell>+/-</Table.HeaderCell>
                  <Table.HeaderCell>Avg Occ % CY</Table.HeaderCell>
                  <Table.HeaderCell>Avg Occ % PY</Table.HeaderCell>
                  <Table.HeaderCell>+/-</Table.HeaderCell>
                  <Table.HeaderCell>ADR CY</Table.HeaderCell>
                  <Table.HeaderCell>ADR PY</Table.HeaderCell>
                  <Table.HeaderCell>+/-</Table.HeaderCell>
                  <Table.HeaderCell>Owner Nights CY</Table.HeaderCell>
                  <Table.HeaderCell>Owner Nights PY</Table.HeaderCell>
                  <Table.HeaderCell>+/-</Table.HeaderCell>
                  <Table.HeaderCell>Owner Use value CY</Table.HeaderCell>
                  <Table.HeaderCell>Owner Use value PY</Table.HeaderCell>
                  <Table.HeaderCell>+/-</Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                <Table.Row>
                  <Table.Cell>Total</Table.Cell>
                  <Table.Cell />
                  <Table.Cell>
                    {reportData != null ? reportData.revenueTotal : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.revenueTotalPy : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.revenueTotalDiff : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.nightsTotalCy : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.nightsTotalPy : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.nightsTotalDiff : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.avgOccTotal : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.avgOccTotalPy : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.avgOccTotalDiff : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.adrTotal : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.adrTotalPy : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.adrTotalDiff : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.ownerNightsTotal : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.ownerNightsTotalPy : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.ownerNightsTotalDiff : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null ? reportData.newOwnerUseValueTotal : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null
                      ? reportData.newOwnerUseValueTotalPy
                      : ''}
                  </Table.Cell>
                  <Table.Cell>
                    {reportData != null
                      ? reportData.newOwnerUseValueTotalDiff
                      : ''}
                  </Table.Cell>
                </Table.Row>
                {reportData
                  ? reportData.propertyRevenueDataList.map(item => (
                      <RoomTypeRow key={item.roomId} item={item} />
                    ))
                  : ''}
              </Table.Body>
            </Table>
          </div>

          <Header as="h2" style={{ marginTop: 28, marginBottom: 28 }}>
            Total:
          </Header>

          <Segment basic style={{ padding: 0, marginBottom: 28 }}>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Room #</Table.HeaderCell>
                  <Table.HeaderCell>Revenue</Table.HeaderCell>
                  <Table.HeaderCell>Total Sales Tax</Table.HeaderCell>
                  <Table.HeaderCell>Total Occupancy Tax</Table.HeaderCell>
                  <Table.HeaderCell>Owner Use Value Rate</Table.HeaderCell>
                  <Table.HeaderCell>Owner Use Value</Table.HeaderCell>
                  <Table.HeaderCell>
                    Total Revenue + Owner Use Value
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {roomData
                  ? roomData.roomRevenueMasterDataList.map(item => (
                      <RoomRow
                        key={item.roomId}
                        item={item}
                        onClick={onRoomClick}
                      />
                    ))
                  : ''}
              </Table.Body>
            </Table>
          </Segment>

          <Segment basic style={{ padding: 0 }}>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Room #</Table.HeaderCell>
                  <Table.HeaderCell>Night</Table.HeaderCell>
                  <Table.HeaderCell>Rate</Table.HeaderCell>
                  <Table.HeaderCell>Reservation #</Table.HeaderCell>
                  <Table.HeaderCell>Owner Use Value</Table.HeaderCell>
                  <Table.HeaderCell>Owner Rate</Table.HeaderCell>
                  <Table.HeaderCell>Occupancy</Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {detailData != null
                  ? detailData.roomRevenueDetailDataList.map(item => (
                      <Table.Row>
                        <Table.Cell>{item.roomNumber}</Table.Cell>
                        <Table.Cell>{item.stayDate}</Table.Cell>
                        <Table.Cell>{item.effectiveRoomRateValue}</Table.Cell>
                        <Table.Cell>{item.reservationId}</Table.Cell>
                        <Table.Cell>{item.ownerUseValueToUse}</Table.Cell>
                        <Table.Cell>{item.ownerRateValue}</Table.Cell>
                        <Table.Cell>
                          {item.occupancyPercentageByPropertyToString}
                        </Table.Cell>
                      </Table.Row>
                    ))
                  : ''}
              </Table.Body>
            </Table>
          </Segment>
        </Container>
      </Dimmer.Dimmable>
    </div>
  );
};

export default React.memo(OwnerPropertyReportView);
